import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  PaperAirplaneIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { groupChatService, type GroupChat, type GroupChatMessage } from '../../services/groupChatService';
import Button from '../ui/Button';
import Card from '../ui/Card';

interface GroupChatInterfaceProps {
  chatId: string;
  currentUserId: string;
  onClose?: () => void;
  className?: string;
}

/**
 * Group Chat Interface Component
 * Real-time group chat with Socket.IO integration
 */
const GroupChatInterface: React.FC<GroupChatInterfaceProps> = ({
  chatId,
  currentUserId,
  onClose,
  className = '',
}) => {
  const [chat, setChat] = useState<GroupChat | null>(null);
  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Load chat and messages
  useEffect(() => {
    loadChat();
    loadMessages();

    // Initialize Socket.IO connection
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';
    const token = localStorage.getItem('accessToken');
    
    const newSocket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    // Join chat room
    newSocket.emit('join-group-chat', chatId);

    // Listen for new messages
    newSocket.on('group-chat-message', (message: GroupChatMessage) => {
      setMessages((prev) => [...prev, message]);
      scrollToBottom();
    });

    // Listen for message updates
    newSocket.on('group-chat-message-updated', (message: GroupChatMessage) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? message : m))
      );
    });

    // Listen for user joined/left
    newSocket.on('group-chat-user-joined', (data: { userId: string; username: string }) => {
      // Handle user joined (optional: show notification)
      console.log(`${data.username} joined the chat`);
    });

    newSocket.on('group-chat-user-left', (data: { userId: string; username: string }) => {
      // Handle user left (optional: show notification)
      console.log(`${data.username} left the chat`);
    });

    socketRef.current = newSocket;

    return () => {
      newSocket.emit('leave-group-chat', chatId);
      newSocket.close();
    };
  }, [chatId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChat = async () => {
    try {
      const res = await groupChatService.getGroupChat(chatId);
      setChat(res.chat);
    } catch (error) {
      console.error('Failed to load chat:', error);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const res = await groupChatService.getMessages(chatId);
      setMessages(res.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await groupChatService.sendMessage(chatId, newMessage.trim());
      // Message will be added via socket event
      setNewMessage('');
    } catch (error: any) {
      console.error('Failed to send message:', error);
      alert(error?.response?.data?.message || 'Failed to send message');
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Card className={`group-chat-interface flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {chat && (
            <>
              <div className="w-10 h-10 bg-patreon-orange rounded-full flex items-center justify-center text-white font-semibold">
                <UserGroupIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{chat.name}</h3>
                {chat._count && (
                  <p className="text-sm text-gray-500">
                    {chat._count.members} members
                  </p>
                )}
              </div>
            </>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        style={{ maxHeight: '500px' }}
      >
        {loading ? (
          <div className="text-center text-gray-500 py-8">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === currentUserId;
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs lg:max-w-md ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                  {!isOwn && message.sender && (
                    <div className="flex items-center gap-2 mb-1">
                      {message.sender.avatar ? (
                        <img
                          src={message.sender.avatar}
                          alt={message.sender.username}
                          className="w-5 h-5 rounded-full"
                        />
                      ) : (
                        <div className="w-5 h-5 bg-gray-300 rounded-full" />
                      )}
                      <span className="text-xs text-gray-500 font-medium">
                        {message.sender.username}
                      </span>
                    </div>
                  )}
                  <div
                    className={`px-4 py-2 rounded-lg ${
                      isOwn
                        ? 'bg-patreon-orange text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {message.content}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 mt-1">
                    {formatTime(message.createdAt)}
                  </span>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
            maxLength={5000}
          />
          <Button
            type="submit"
            disabled={!newMessage.trim()}
            leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
          >
            Send
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default GroupChatInterface;

